import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  StyledModal,
  StyledModalHeader,
  MainButton,
} from '../../../../common/form/FormElements.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import { required } from '../../../../antd/form/FormValidation.js';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
} from '../../../../../redux/services/ing_projects.jsx';

/**
 * Modal for creating or editing a student application to a project.
 *
 * Props:
 * - visible: boolean - whether modal is open
 * - onClose: function - callback when modal closes
 * - project: object - the project to apply to (required for create mode)
 * - application: object - existing application to edit (optional, enables edit mode)
 */
const ApplyToProjectModal = ({ visible, onClose, project, application }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { apiErrorMessage, successMessage } = useToastMessages();

  const isEditMode = !!application;

  const priorityOptions = React.useMemo(
    () => [
      { value: 1, label: `1 - ${t('Najvyššia priorita')}` },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: `5 - ${t('Najnižšia priorita')}` },
    ],
    [t],
  );

  const [createApplication, { isLoading: isCreating }] =
    useCreateApplicationMutation();

  const [updateApplication, { isLoading: isUpdating }] =
    useUpdateApplicationMutation();

  // Použijeme študijné programy definované pre projekt
  // Prioritne použijeme project prop (ak je dostupný), inak application.project
  const studyProgramOptions = React.useMemo(() => {
    const projectToUse = project || application?.project;
    if (!projectToUse?.study_programs) return [];
    return projectToUse.study_programs.map((program) => ({
      value: program.id,
      label: program.name,
    }));
  }, [project, application]);

  const isLoadingPrograms = false;

  // Prefill form when editing
  useEffect(() => {
    if (visible && application) {
      form.setFieldsValue({
        study_program_id: application.study_program_id,
        priority: application.priority,
      });
    } else if (visible && !application) {
      form.resetFields();
    }
  }, [visible, application, form]);

  const handleFinish = async (values) => {
    const projectId = project?.id || application?.ip_project_id;

    try {
      if (isEditMode) {
        await updateApplication({
          applicationId: application.id,
          body: {
            ip_project_id: projectId,
            study_program_id: values.study_program_id,
            priority: values.priority,
          },
        }).unwrap();

        successMessage({ message: t('Prihláška bola úspešne upravená.') });
      } else {
        await createApplication({
          ip_project_id: projectId,
          study_program_id: values.study_program_id,
          priority: values.priority,
        }).unwrap();

        successMessage({ message: t('Prihláška bola úspešne vytvorená.') });
      }

      form.resetFields();
      onClose();
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Need either project (create) or application (edit)
  if (!project && !application) return null;

  const isSubmitting = isCreating || isUpdating;

  return (
    <StyledModal open={visible} size="md" onCancel={handleCancel} footer={null}>
      <StyledModalHeader>
        {isEditMode ? t('Upraviť prihlášku') : t('Prihlásiť sa na projekt')}
      </StyledModalHeader>
      <div style={{ padding: '10px' }}>
        <Form form={form} onFinish={handleFinish} layout="vertical">
          <FormSelect
            name="study_program_id"
            label={t('Študijný program')}
            options={studyProgramOptions}
            rules={[required(t)]}
            placeholder={t('Vyberte študijný program')}
            loading={isLoadingPrograms}
          />

          <FormSelect
            name="priority"
            label={t('Priorita')}
            options={priorityOptions}
            rules={[required(t)]}
            placeholder={t('Vyberte prioritu')}
          />

          <FormButtonsContainer>
            <MainButton onClick={handleCancel}>{t('Zrušiť')}</MainButton>
            <MainButton htmlType="submit" loading={isSubmitting}>
              {isEditMode ? t('Uložiť') : t('Prihlásiť sa')}
            </MainButton>
          </FormButtonsContainer>
        </Form>
      </div>
    </StyledModal>
  );
};

ApplyToProjectModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object,
  application: PropTypes.object,
};

export default ApplyToProjectModal;
